import axios from 'axios';

/** @see https://docs.stripe.com/api */
const STRIPE_V1 = 'https://api.stripe.com/v1';

export function stripeHeaders(secretKey: string): Record<string, string> {
  return { Authorization: `Bearer ${secretKey.trim()}` };
}

export function stripeDashboardBase(secretKey: string): string {
  const k = secretKey.trim();
  return k.startsWith('sk_live') ? 'https://dashboard.stripe.com' : 'https://dashboard.stripe.com/test';
}

export type StripeList<T> = {
  object: 'list';
  data: T[];
  has_more: boolean;
  url: string;
};

export type StripeSearchResult<T> = {
  object: 'search_result';
  data: T[];
  has_more: boolean;
  next_page?: string | null;
};

export type StripeBalance = {
  object: string;
  available: Array<{ amount: number; currency: string }>;
  pending: Array<{ amount: number; currency: string }>;
};

export type StripeCustomer = {
  id: string;
  object: 'customer';
  email?: string | null;
  name?: string | null;
  created: number;
  description?: string | null;
  currency?: string | null;
  balance?: number;
  delinquent?: boolean;
};

export type StripeProduct = {
  id: string;
  object: 'product';
  name?: string | null;
  description?: string | null;
};

export type StripePrice = {
  id: string;
  unit_amount: number | null;
  currency: string;
  nickname?: string | null;
  recurring?: { interval: string; interval_count?: number } | null;
  product?: string | StripeProduct | null;
};

export type StripeSubscription = {
  id: string;
  object: 'subscription';
  status: string;
  customer: string | StripeCustomer;
  current_period_start: number;
  current_period_end: number;
  cancel_at_period_end: boolean;
  canceled_at: number | null;
  items: {
    data: Array<{
      id: string;
      quantity?: number | null;
      price?: StripePrice | null;
    }>;
  };
  currency?: string;
};

export type StripeInvoice = {
  id: string;
  object: 'invoice';
  customer: string | StripeCustomer | null;
  subscription?: string | null;
  status: string | null;
  number?: string | null;
  currency: string;
  total: number;
  amount_paid: number;
  amount_due: number;
  created: number;
  hosted_invoice_url?: string | null;
  invoice_pdf?: string | null;
};

export type StripeCharge = {
  id: string;
  object: 'charge';
  amount: number;
  amount_refunded: number;
  currency: string;
  paid: boolean;
  status: string;
  created: number;
  description?: string | null;
  customer?: string | StripeCustomer | null;
  receipt_email?: string | null;
  receipt_url?: string | null;
  failure_message?: string | null;
};

function buildStripeQueryString(query: Record<string, string | number | undefined>, expand?: string[]): string {
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === '') {
      continue;
    }
    usp.append(k, String(v));
  }
  if (expand?.length) {
    for (const e of expand) {
      usp.append('expand[]', e);
    }
  }
  return usp.toString();
}

async function stripeGet<T>(secretKey: string, path: string, params?: Record<string, string | number | undefined>): Promise<T> {
  const { data } = await axios.get<T>(`${STRIPE_V1}${path}`, {
    headers: stripeHeaders(secretKey),
    params,
  });
  return data;
}

export async function stripeGetExpanded<T>(
  secretKey: string,
  path: string,
  query: Record<string, string | number | undefined>,
  expand?: string[]
): Promise<T> {
  const qs = buildStripeQueryString(query, expand);
  const url = qs ? `${STRIPE_V1}${path}?${qs}` : `${STRIPE_V1}${path}`;
  const { data } = await axios.get<T>(url, { headers: stripeHeaders(secretKey) });
  return data;
}

/** Expands used when listing subscriptions (names for UI). */
export const STRIPE_SUB_LIST_EXPAND = ['data.customer', 'data.items.data.price.product'] as const;

/** Paid invoice line for subscription / customer revenue UI. */
export type StripeInvoiceTxn = {
  id: string;
  created: number;
  amountPaid: number;
  currency: string;
  number: string | null;
};

export type StripePaidAgg = {
  totalCents: number;
  currency: string;
  transactions: StripeInvoiceTxn[];
};

export function aggregatePaidInvoicesBySubscription(invoices: StripeInvoice[]): Map<string, StripePaidAgg> {
  const map = new Map<string, StripePaidAgg>();
  for (const inv of invoices) {
    if (inv.status !== 'paid' || (inv.amount_paid ?? 0) <= 0) {
      continue;
    }
    const subId = inv.subscription;
    if (!subId || typeof subId !== 'string') {
      continue;
    }
    const prev = map.get(subId) ?? { totalCents: 0, currency: inv.currency, transactions: [] };
    prev.totalCents += inv.amount_paid;
    prev.currency = inv.currency;
    prev.transactions.push({
      id: inv.id,
      created: inv.created,
      amountPaid: inv.amount_paid,
      currency: inv.currency,
      number: inv.number ?? null,
    });
    map.set(subId, prev);
  }
  Array.from(map.values()).forEach((agg) => {
    agg.transactions.sort((a, b) => b.created - a.created);
  });
  return map;
}

export function aggregatePaidInvoicesByCustomer(invoices: StripeInvoice[]): Map<string, StripePaidAgg> {
  const map = new Map<string, StripePaidAgg>();
  for (const inv of invoices) {
    if (inv.status !== 'paid' || (inv.amount_paid ?? 0) <= 0) {
      continue;
    }
    const cust = inv.customer;
    const custId = typeof cust === 'string' ? cust : cust?.id;
    if (!custId) {
      continue;
    }
    const prev = map.get(custId) ?? { totalCents: 0, currency: inv.currency, transactions: [] };
    prev.totalCents += inv.amount_paid;
    prev.currency = inv.currency;
    prev.transactions.push({
      id: inv.id,
      created: inv.created,
      amountPaid: inv.amount_paid,
      currency: inv.currency,
      number: inv.number ?? null,
    });
    map.set(custId, prev);
  }
  Array.from(map.values()).forEach((agg) => {
    agg.transactions.sort((a, b) => b.created - a.created);
  });
  return map;
}

export function formatCustomerDisplay(c: string | StripeCustomer | null | undefined): string {
  if (c == null) {
    return 'Customer';
  }
  if (typeof c === 'string') {
    return 'Customer';
  }
  const parts = [c.name?.trim(), c.email?.trim()].filter(Boolean);
  return parts.length ? parts.join(' · ') : 'Customer (no name on file)';
}

export function formatSubscriptionPlanDisplay(sub: StripeSubscription): string {
  const labels: string[] = [];
  for (const line of sub.items?.data || []) {
    const p = line.price;
    if (!p) {
      continue;
    }
    const nick = p.nickname?.trim();
    const prod = p.product;
    const prodName = typeof prod === 'object' && prod && 'name' in prod ? String((prod as StripeProduct).name || '').trim() : '';
    const label = nick || prodName;
    if (label) {
      labels.push(label);
    }
  }
  if (labels.length) {
    return Array.from(new Set(labels)).join(' + ');
  }
  const p0 = sub.items?.data?.[0]?.price;
  if (p0?.unit_amount != null && p0.recurring) {
    const ic = p0.recurring.interval_count ?? 1;
    const intv = p0.recurring.interval;
    return `${ic} ${intv} · ${(p0.unit_amount / 100).toFixed(2)} ${p0.currency.toUpperCase()}`;
  }
  return 'Subscription';
}

export function formatChargeDisplay(ch: StripeCharge): string {
  const d = ch.description?.trim();
  if (d) {
    return d;
  }
  return 'Card payment';
}

/** Fetch recent paid invoices for revenue / transaction rollups (paginates up to `max`). */
export async function listStripePaidInvoicesBatch(secretKey: string, max = 100): Promise<StripeInvoice[]> {
  const out: StripeInvoice[] = [];
  let starting_after: string | undefined;
  while (out.length < max) {
    const take = Math.min(100, max - out.length);
    const page = await stripeGetExpanded<StripeList<StripeInvoice>>(
      secretKey,
      '/invoices',
      { limit: take, status: 'paid', starting_after },
      ['data.customer']
    );
    out.push(...page.data);
    if (!page.has_more || page.data.length === 0) {
      break;
    }
    starting_after = page.data[page.data.length - 1].id;
  }
  return out;
}

export async function fetchStripeBalance(secretKey: string): Promise<StripeBalance | null> {
  if (!secretKey.trim()) {
    return null;
  }
  return stripeGet<StripeBalance>(secretKey, '/balance');
}

export async function listStripeCustomers(
  secretKey: string,
  opts?: { limit?: number; starting_after?: string }
): Promise<StripeList<StripeCustomer>> {
  const limit = opts?.limit ?? 25;
  return stripeGet<StripeList<StripeCustomer>>(secretKey, '/customers', {
    limit,
    starting_after: opts?.starting_after,
  });
}

/** Stripe search query literals use single quotes — escape user input. */
export function buildCustomerSearchQuery(userInput: string): string {
  const s = userInput.trim();
  if (!s) {
    return '';
  }
  const e = s.replace(/\\/g, '\\\\').replace(/'/g, "\\'");
  return `name~'${e}' OR email~'${e}'`;
}

/** @see https://docs.stripe.com/api/customers/search */
export async function searchStripeCustomers(
  secretKey: string,
  query: string,
  limit = 25
): Promise<StripeSearchResult<StripeCustomer>> {
  const q = query.trim();
  if (!q) {
    return { object: 'search_result', data: [], has_more: false };
  }
  return stripeGet<StripeSearchResult<StripeCustomer>>(secretKey, '/customers/search', {
    query: q,
    limit,
  });
}

export async function listStripeSubscriptions(
  secretKey: string,
  opts?: { limit?: number; starting_after?: string; status?: string }
): Promise<StripeList<StripeSubscription>> {
  const limit = opts?.limit ?? 25;
  return stripeGetExpanded<StripeList<StripeSubscription>>(
    secretKey,
    '/subscriptions',
    {
      limit,
      starting_after: opts?.starting_after,
      ...(opts?.status ? { status: opts.status } : {}),
    },
    [...STRIPE_SUB_LIST_EXPAND]
  );
}

export async function listStripeInvoices(
  secretKey: string,
  opts?: { limit?: number; starting_after?: string; status?: string }
): Promise<StripeList<StripeInvoice>> {
  const limit = opts?.limit ?? 25;
  return stripeGetExpanded<StripeList<StripeInvoice>>(
    secretKey,
    '/invoices',
    {
      limit,
      starting_after: opts?.starting_after,
      status: opts?.status,
    },
    ['data.customer']
  );
}

export async function listStripeCharges(
  secretKey: string,
  opts?: {
    limit?: number;
    starting_after?: string;
    createdGte?: number;
    createdLte?: number;
  }
): Promise<StripeList<StripeCharge>> {
  const limit = opts?.limit ?? 25;
  return stripeGetExpanded<StripeList<StripeCharge>>(
    secretKey,
    '/charges',
    {
      limit,
      starting_after: opts?.starting_after,
      'created[gte]': opts?.createdGte,
      'created[lte]': opts?.createdLte,
    },
    ['data.customer']
  );
}

export async function listPaidChargesInRange(
  secretKey: string,
  createdGte: number,
  createdLte: number
): Promise<Array<{ created: number; amount: number; currency: string }>> {
  const out: Array<{ created: number; amount: number; currency: string }> = [];
  let starting_after: string | undefined;
  for (let i = 0; i < 10; i++) {
    const page = await listStripeCharges(secretKey, {
      limit: 100,
      starting_after,
      createdGte,
      createdLte,
    });
    for (const c of page.data) {
      if (c.paid) {
        out.push({ created: c.created, amount: c.amount, currency: c.currency });
      }
    }
    if (!page.has_more || page.data.length === 0) {
      break;
    }
    starting_after = page.data[page.data.length - 1].id;
  }
  return out;
}

export async function listStripeInvoicesIssuedInRange(
  secretKey: string,
  createdGte: number,
  createdLte: number
): Promise<Array<{ created: number; amount: number }>> {
  const out: Array<{ created: number; amount: number }> = [];
  let starting_after: string | undefined;
  for (let i = 0; i < 10; i++) {
    const page = await stripeGetExpanded<StripeList<{ id: string; created: number; total: number; status: string | null }>>(
      secretKey,
      '/invoices',
      {
        limit: 100,
        starting_after,
        'created[gte]': createdGte,
        'created[lte]': createdLte,
      }
    );
    for (const inv of page.data) {
      const status = inv.status || '';
      if (status === 'void' || status === 'uncollectible') {
        continue;
      }
      if (inv.total > 0) {
        out.push({ created: inv.created, amount: inv.total });
      }
    }
    if (!page.has_more || page.data.length === 0) {
      break;
    }
    starting_after = page.data[page.data.length - 1].id;
  }
  return out;
}

export type StripePayout = {
  id: string;
  amount: number;
  currency: string;
  status: string;
  arrival_date: number;
  created: number;
  method?: string;
  type?: string;
  description?: string | null;
};

export async function listStripePayouts(
  secretKey: string,
  opts?: { limit?: number; starting_after?: string; createdGte?: number; createdLte?: number }
): Promise<StripeList<StripePayout>> {
  const limit = opts?.limit ?? 25;
  return stripeGet<StripeList<StripePayout>>(secretKey, '/payouts', {
    limit,
    starting_after: opts?.starting_after,
    'created[gte]': opts?.createdGte,
    'created[lte]': opts?.createdLte,
  });
}

/** Approximate monthly recurring revenue in major units (dollars), single-currency bias (first price currency). */
export function estimateMrrFromSubscription(sub: StripeSubscription): { cents: number; currency: string } {
  let cents = 0;
  let currency = 'usd';
  for (const item of sub.items?.data || []) {
    const price = item.price;
    if (!price || price.unit_amount == null) {
      continue;
    }
    currency = price.currency || currency;
    const line = price.unit_amount * (item.quantity ?? 1);
    const interval = price.recurring?.interval ?? 'month';
    const ic = price.recurring?.interval_count ?? 1;
    switch (interval) {
      case 'year':
        cents += Math.round(line / (12 * ic));
        break;
      case 'week':
        cents += Math.round((line * 52) / (12 * ic));
        break;
      case 'day':
        cents += Math.round((line * 30) / ic);
        break;
      default:
        cents += Math.round(line / ic);
    }
  }
  return { cents, currency };
}

export async function fetchStripeOverviewMetrics(secretKey: string): Promise<{
  balance: StripeBalance | null;
  mrrCents: number;
  mrrCurrency: string;
  activeSubscriptions: number;
  customersOnPage: number;
  customersHasMore: boolean;
  paidCharges30dCents: number;
  paidCharges30dCount: number;
  chargesCurrency: string;
}> {
  const now = Math.floor(Date.now() / 1000);
  const thirtyDaysAgo = now - 30 * 24 * 60 * 60;

  const emptyList = <T>(): StripeList<T> => ({
    object: 'list',
    data: [],
    has_more: false,
    url: '',
  });

  const [balance, subs, customers, charges] = await Promise.all([
    fetchStripeBalance(secretKey).catch(() => null),
    listStripeSubscriptions(secretKey, { status: 'active', limit: 100 }).catch(() => emptyList<StripeSubscription>()),
    listStripeCustomers(secretKey, { limit: 100 }).catch(() => emptyList<StripeCustomer>()),
    listStripeCharges(secretKey, { limit: 100, createdGte: thirtyDaysAgo }).catch(() => emptyList<StripeCharge>()),
  ]);

  let mrrCents = 0;
  let mrrCurrency = 'usd';
  for (const s of subs.data) {
    const { cents, currency } = estimateMrrFromSubscription(s);
    mrrCents += cents;
    mrrCurrency = currency || mrrCurrency;
  }

  let paidCharges30dCents = 0;
  let chargesCurrency = 'usd';
  let paidCharges30dCount = 0;
  for (const c of charges.data) {
    if (c.paid && c.amount != null) {
      paidCharges30dCents += c.amount;
      chargesCurrency = c.currency || chargesCurrency;
      paidCharges30dCount += 1;
    }
  }

  return {
    balance,
    mrrCents,
    mrrCurrency,
    activeSubscriptions: subs.data.length,
    customersOnPage: customers.data.length,
    customersHasMore: customers.has_more,
    paidCharges30dCents,
    paidCharges30dCount,
    chargesCurrency,
  };
}

export type StripeOverviewMetrics = Awaited<ReturnType<typeof fetchStripeOverviewMetrics>>;
